import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Building2, BriefcaseBusiness, CalendarCheck, CircleDollarSign, Clock3, Download, GitBranchPlus, ListChecks, MessageCircle, Paperclip, Pencil, Plus, RefreshCw, RotateCcw, Search, Trash2, Upload, UserPlus, Users, X } from "lucide-react";
import {
    archiveCrmActivity,
    archiveCrmCompany,
    archiveCrmContact,
    archiveCrmDeal,
    archiveCrmLead,
    archiveCrmTask,
    convertCrmLead,
    createCrmActivity,
    createCrmCompany,
    createCrmContact,
    createCrmDeal,
    createCrmLead,
    createCrmTask,
    deleteCrmActivity,
    deleteCrmCompany,
    deleteCrmContact,
    deleteCrmDeal,
    deleteCrmLead,
    deleteCrmTask,
    editCrmActivity,
    editCrmCompany,
    editCrmContact,
    editCrmDeal,
    editCrmLead,
    editCrmTask,
    getAssignableUsers,
    getCrmActivities,
    getCrmCompanyTimeline,
    getCrmRecordAttachmentDownloadUrl,
    getCrmRecordAttachments,
    getCrmCompanies,
    getCrmContacts,
    getCrmDeals,
    getCrmLeads,
    getCrmReports,
    getCrmSummary,
    getCrmTasks,
    searchCatalogProducts,
    searchCrm,
    uploadCrmRecordAttachment,
    deleteCrmRecordAttachment,
    updateCrmTaskStatus,
    updateAzsRequestStatus,
} from "../services/api";

const emptyCompany = {
    name: "",
    legal_name: "",
    status: "active",
    phone: "",
    email: "",
    website: "",
    address: "",
};
const emptyContact = {
    company_id: "",
    first_name: "",
    last_name: "",
    position: "",
    phone: "",
    email: "",
    status: "active",
};
const emptyActivity = { company_id: "", contact_id: "", type: "note", title: "", body: "" };
const emptyLead = {
    title: "",
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    source: "manual",
    status: "new",
    score: "",
    estimated_amount: "",
    notes: "",
    items: [],
};
const emptyLeadConvert = { company_id: "", contact_id: "" };
const emptyDeal = {
    title: "",
    company_id: "",
    contact_id: "",
    stage: "new",
    amount: "",
    probability: 10,
    expected_close_at: "",
    description: "",
};
const emptyTask = {
    title: "",
    body: "",
    status: "open",
    priority: "normal",
    due_at: "",
    assigned_to_username: "",
    company_id: "",
    contact_id: "",
    deal_id: "",
    lead_id: "",
};

const statusLabels = {
    active: "Активна",
    paused: "На паузе",
    archived: "Архив",
};

const activityTypeLabels = {
    note: "Заметка",
    call: "Звонок",
    meeting: "Встреча",
    task: "Задача",
};

const leadStatusLabels = {
    new: "Новый",
    contacted: "Связались",
    qualified: "Квалифицирован",
    converted: "Конвертирован",
    lost: "Потерян",
};

const azsStatusLabels = {
    new: "Новая",
    in_work: "В работе",
    checking: "Проверка",
    waiting_supplier: "Ждём поставщика",
    offer_preparing: "Готовим КП",
    offer_sent: "КП отправлено",
    waiting_customer: "Ждём клиента",
    completed: "Завершена",
    cancelled: "Отменена",
};
const azsStatusOptions = Object.entries(azsStatusLabels).map(([value, label]) => ({ value, label }));
const azsPublicStatusLabels = {
    received: "Получена",
    processing: "В обработке",
    cancelled: "Отменена",
};

const dealStages = [
    { value: "new", label: "Новая" },
    { value: "qualified", label: "Квалификация" },
    { value: "proposal", label: "Предложение" },
    { value: "won", label: "Выиграна" },
    { value: "lost", label: "Потеряна" },
];
const taskStatusLabels = {
    open: "Открыта",
    in_progress: "В работе",
    blocked: "Блокер",
    done: "Готово",
};
const taskPriorityLabels = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};
const timelineSourceLabels = {
    activity: "Активность",
    task: "Задача",
    deal: "Сделка",
    lead: "Лид",
    attachment: "Файл",
};
const searchTypeLabels = {
    company: "Компания",
    contact: "Контакт",
    lead: "Лид",
    deal: "Сделка",
    task: "Задача",
};

const normalizeFormValue = (value) => value ?? "";
const normalizeDecimalValue = (value) => {
    const normalized = String(value ?? "").trim().replace(/\s+/g, "").replace(",", ".");
    return normalized;
};
const parseNullableAmount = (value) => {
    const normalized = normalizeDecimalValue(value);
    if (!normalized) {
        return null;
    }
    return Number.isFinite(Number(normalized)) ? normalized : null;
};
const formatMoney = (value) => (value ? `${Number(value).toLocaleString("ru-RU")} ₽` : "0 ₽");
const formatDateTime = (value) => (value ? new Date(value).toLocaleString("ru-RU") : "");
const formatFileSize = (size = 0) => {
    if (size < 1024) {
        return `${size} Б`;
    }
    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} КБ`;
    }
    return `${(size / 1024 / 1024).toFixed(1)} МБ`;
};
const sumValues = (values = {}) => Object.values(values || {}).reduce((sum, value) => sum + Number(value || 0), 0);
const maxAttachmentSize = 25 * 1024 * 1024;

const itemCatalogProduct = (item = {}) => item.catalog_product || {};
const itemDisplayName = (item = {}) => (
    itemCatalogProduct(item).name || item.name || item.sku || item.internal_code || item.id || "Товар без названия"
);
const itemDisplayAmount = (item = {}) => {
    const quantity = item.quantity || 1;
    const priceValue = item.price || itemCatalogProduct(item).price;
    const price = priceValue ? ` · ${Number(priceValue).toLocaleString("ru-RU")} ₽` : "";
    return `${quantity} шт.${price}`;
};
const itemDisplayCode = (item = {}) => (
    item.internal_code || item.sku || itemCatalogProduct(item).external_id || itemCatalogProduct(item).vendor_code || item.id || ""
);
const itemDisplayParams = (item = {}) => (
    Array.isArray(item.selected_parameters)
        ? item.selected_parameters
            .map((param) => [param?.name, param?.value].filter(Boolean).join(": "))
            .filter(Boolean)
            .join(" / ")
        : ""
);
const itemDisplayUrl = (item = {}) => item.url || itemCatalogProduct(item).url;
const productToLeadItem = (product = {}) => ({
    catalog_product_id: product.catalog_product_id,
    id: product.external_id || product.id || "",
    internal_code: product.external_id || product.vendor_code || "",
    sku: product.vendor_code || product.external_id || "",
    name: product.name || "",
    quantity: "1",
    price: product.price || "",
    url: product.url || "",
    selected_parameters: [],
    catalog_product: product,
});
const leadItemIdentity = (item = {}) => (
    item.catalog_product_id || item.internal_code || item.sku || item.id || item.url || item.name || ""
);
const calculateLeadItemsTotal = (items = []) => {
    let total = 0;
    let hasAmount = false;
    (items || []).forEach((item) => {
        const price = Number(normalizeDecimalValue(item.price || itemCatalogProduct(item).price));
        const quantity = Number(normalizeDecimalValue(item.quantity || 1));
        if (Number.isFinite(price) && Number.isFinite(quantity)) {
            total += price * quantity;
            hasAmount = true;
        }
    });
    return hasAmount ? String(Math.round(total * 100) / 100) : "";
};
const leadDetailsLines = (lead = {}) => (
    String(lead.notes || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .filter((line) => !/^Заявка AZS Parts:/i.test(line) && !/^Тип:/i.test(line) && !/^Статус сайта:/i.test(line))
);
const leadRequestTextLines = (lead = {}) => {
    if (lead.azs_message) {
        return [lead.azs_message];
    }
    const hasStructuredItems = (lead.azs_items || []).length > 0;
    return leadDetailsLines(lead)
        .filter((line) => !(hasStructuredItems && /^\d+\./.test(line)))
        .slice(0, 3);
};

const CrmHome = () => {
    const [summary, setSummary] = useState({ companies: 0, contacts: 0, activities: 0, leads: 0, deals: 0, open_leads: 0, open_tasks: 0, active_deals_amount: 0 });
    const [reports, setReports] = useState({
        deals_by_stage: {},
        deal_amount_by_stage: {},
        deals_by_owner: {},
        active_deal_amount_by_owner: {},
        tasks_by_status: {},
        tasks_by_owner: {},
        overdue_tasks: 0,
        due_today_tasks: 0,
        urgent_open_tasks: 0,
    });
    const [companies, setCompanies] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [activities, setActivities] = useState([]);
    const [leads, setLeads] = useState([]);
    const [deals, setDeals] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [assignableUsers, setAssignableUsers] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companyForm, setCompanyForm] = useState(emptyCompany);
    const [contactForm, setContactForm] = useState(emptyContact);
    const [activityForm, setActivityForm] = useState(emptyActivity);
    const [leadForm, setLeadForm] = useState(emptyLead);
    const [leadProductQuery, setLeadProductQuery] = useState("");
    const [leadProductResults, setLeadProductResults] = useState([]);
    const [isLeadProductSearching, setIsLeadProductSearching] = useState(false);
    const [dealForm, setDealForm] = useState(emptyDeal);
    const [taskForm, setTaskForm] = useState(emptyTask);
    const [editingCompanyId, setEditingCompanyId] = useState(null);
    const [editingContactId, setEditingContactId] = useState(null);
    const [editingActivityId, setEditingActivityId] = useState(null);
    const [editingLeadId, setEditingLeadId] = useState(null);
    const [isLeadCreatorOpen, setIsLeadCreatorOpen] = useState(false);
    const [leadConvertForm, setLeadConvertForm] = useState(emptyLeadConvert);
    const [editingDealId, setEditingDealId] = useState(null);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showArchivedCrm, setShowArchivedCrm] = useState(false);
    const [ownerFilter, setOwnerFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [timelineItems, setTimelineItems] = useState([]);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [recordAttachments, setRecordAttachments] = useState({});
    const [attachmentUploads, setAttachmentUploads] = useState({});
    const [quickNoteBody, setQuickNoteBody] = useState("");
    const [isQuickNoteSaving, setIsQuickNoteSaving] = useState(false);
    const [azsStatusDrafts, setAzsStatusDrafts] = useState({});
    const [azsStatusSaving, setAzsStatusSaving] = useState({});
    const [message, setMessage] = useState("");

    const selectedCompany = useMemo(
        () => companies.find((company) => company.id === selectedCompanyId) || companies[0],
        [companies, selectedCompanyId]
    );
    const selectedCompanyRecordId = selectedCompany?.id;

    const selectedCompanyContacts = useMemo(() => (
        selectedCompany ? contacts.filter((contact) => contact.company_id === selectedCompany.id) : contacts
    ), [contacts, selectedCompany]);

    const selectedCompanyDeals = useMemo(() => (
        selectedCompany ? deals.filter((deal) => deal.company_id === selectedCompany.id) : deals
    ), [deals, selectedCompany]);

    const selectedCompanyActivities = useMemo(() => (
        selectedCompany ? activities.filter((activity) => activity.company_id === selectedCompany.id) : activities
    ), [activities, selectedCompany]);

    const selectedCompanyLeads = useMemo(() => (
        selectedCompany
            ? leads.filter((lead) => lead.converted_company_id === selectedCompany.id || lead.company_name === selectedCompany.name)
            : leads
    ), [leads, selectedCompany]);

    const selectedCompanyTasks = useMemo(() => (
        selectedCompany
            ? tasks.filter((task) => (
                task.company_id === selectedCompany.id
                || selectedCompanyContacts.some((contact) => contact.id === task.contact_id)
                || selectedCompanyDeals.some((deal) => deal.id === task.deal_id)
                || selectedCompanyLeads.some((lead) => lead.id === task.lead_id)
            ))
            : tasks
    ), [tasks, selectedCompany, selectedCompanyContacts, selectedCompanyDeals, selectedCompanyLeads]);

    const visibleLeads = useMemo(() => (
        ownerFilter ? leads.filter((lead) => lead.owner_username === ownerFilter) : leads
    ), [leads, ownerFilter]);

    const visibleSelectedCompanyDeals = useMemo(() => (
        ownerFilter ? selectedCompanyDeals.filter((deal) => deal.owner_username === ownerFilter) : selectedCompanyDeals
    ), [selectedCompanyDeals, ownerFilter]);

    const visibleSelectedCompanyTasks = useMemo(() => (
        ownerFilter ? selectedCompanyTasks.filter((task) => task.assigned_to_username === ownerFilter) : selectedCompanyTasks
    ), [selectedCompanyTasks, ownerFilter]);

    const visibleSelectedCompanyOpenTasks = useMemo(() => (
        visibleSelectedCompanyTasks.filter((task) => task.status !== "done")
    ), [visibleSelectedCompanyTasks]);

    const selectedCompanyActiveDealAmount = useMemo(() => (
        visibleSelectedCompanyDeals
            .filter((deal) => !["won", "lost"].includes(deal.stage))
            .reduce((sum, deal) => sum + Number(deal.amount || 0), 0)
    ), [visibleSelectedCompanyDeals]);

    const dealCompanyId = dealForm.company_id || selectedCompany?.id || "";
    const dealContactOptions = useMemo(() => (
        dealCompanyId ? contacts.filter((contact) => contact.company_id === Number(dealCompanyId)) : contacts
    ), [contacts, dealCompanyId]);

    const taskCompanyId = taskForm.company_id || (!editingTaskId ? selectedCompany?.id : "") || "";
    const taskContactOptions = useMemo(() => (
        taskCompanyId ? contacts.filter((contact) => contact.company_id === Number(taskCompanyId)) : contacts
    ), [contacts, taskCompanyId]);
    const leadConvertCompanyId = leadConvertForm.company_id || "";
    const leadConvertContactOptions = useMemo(() => (
        leadConvertCompanyId ? contacts.filter((contact) => contact.company_id === Number(leadConvertCompanyId)) : contacts
    ), [contacts, leadConvertCompanyId]);
    const taskDealOptions = useMemo(() => (
        taskCompanyId ? deals.filter((deal) => deal.company_id === Number(taskCompanyId)) : deals
    ), [deals, taskCompanyId]);

    const editingDeal = useMemo(
        () => deals.find((deal) => deal.id === editingDealId),
        [deals, editingDealId]
    );
    const editingTask = useMemo(
        () => tasks.find((task) => task.id === editingTaskId),
        [tasks, editingTaskId]
    );

    const loadCrm = useCallback(async () => {
        setIsLoading(true);
        setMessage("");
        try {
            const archiveParams = showArchivedCrm ? { include_archived: true } : {};
            const ownerParams = ownerFilter ? { owner_username: ownerFilter } : {};
            const taskOwnerParams = ownerFilter ? { assigned_to: ownerFilter } : {};
            const [summaryResponse, reportsResponse, companiesResponse, contactsResponse, activitiesResponse, leadsResponse, dealsResponse, tasksResponse, usersResponse] = await Promise.all([
                getCrmSummary(),
                getCrmReports(ownerParams),
                getCrmCompanies(archiveParams),
                getCrmContacts(archiveParams),
                getCrmActivities(archiveParams),
                getCrmLeads({ ...archiveParams, ...ownerParams }),
                getCrmDeals({ ...archiveParams, ...ownerParams }),
                getCrmTasks({ ...archiveParams, ...taskOwnerParams }),
                getAssignableUsers(),
            ]);
            setSummary(summaryResponse.data);
            setReports(reportsResponse.data);
            setCompanies(companiesResponse.data);
            setContacts(contactsResponse.data);
            setActivities(activitiesResponse.data);
            setLeads(leadsResponse.data);
            setDeals(dealsResponse.data);
            setTasks(tasksResponse.data);
            setAssignableUsers(usersResponse.data);
            setSelectedCompanyId((current) => (
                companiesResponse.data.some((company) => company.id === current)
                    ? current
                    : companiesResponse.data[0]?.id || null
            ));
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить CRM.");
        } finally {
            setIsLoading(false);
        }
    }, [showArchivedCrm, ownerFilter]);

    useEffect(() => {
        loadCrm();
    }, [loadCrm]);

    const attachmentKey = (entityType, entityId) => `${entityType}:${entityId}`;

    const loadRecordAttachments = useCallback(async (entityType, entityId) => {
        if (!entityType || !entityId) {
            return;
        }
        try {
            const response = await getCrmRecordAttachments(entityType, entityId);
            setRecordAttachments((current) => ({
                ...current,
                [attachmentKey(entityType, entityId)]: response.data,
            }));
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить файлы.");
        }
    }, []);

    const loadCompanyTimeline = useCallback(async (companyId) => {
        if (!companyId) {
            setTimelineItems([]);
            return;
        }
        setIsTimelineLoading(true);
        try {
            const response = await getCrmCompanyTimeline(companyId, {
                include_archived: showArchivedCrm,
                limit: 40,
            });
            setTimelineItems(response.data);
        } catch (err) {
            setTimelineItems([]);
            setMessage(err.response?.data?.detail || "Не удалось загрузить историю клиента.");
        } finally {
            setIsTimelineLoading(false);
        }
    }, [showArchivedCrm]);

    const handleUploadRecordAttachment = async (entityType, entityId, file) => {
        if (!file) {
            return;
        }
        if (file.size > maxAttachmentSize) {
            setMessage("Файл больше 25 МБ. Загрузка отменена.");
            return;
        }

        const key = attachmentKey(entityType, entityId);
        setAttachmentUploads((current) => ({ ...current, [key]: true }));
        try {
            const response = await uploadCrmRecordAttachment(entityType, entityId, file);
            setRecordAttachments((current) => ({
                ...current,
                [key]: [response.data, ...(current[key] || [])],
            }));
            if (entityType === "company" && entityId === selectedCompanyRecordId) {
                await loadCompanyTimeline(entityId);
            }
            setMessage("Файл загружен.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось загрузить файл.");
        } finally {
            setAttachmentUploads((current) => ({ ...current, [key]: false }));
        }
    };

    const handleDeleteRecordAttachment = async (entityType, entityId, attachment) => {
        if (!window.confirm(`Удалить файл "${attachment.original_filename}"?`)) {
            return;
        }
        try {
            await deleteCrmRecordAttachment(entityType, entityId, attachment.id);
            const key = attachmentKey(entityType, entityId);
            setRecordAttachments((current) => ({
                ...current,
                [key]: (current[key] || []).filter((item) => item.id !== attachment.id),
            }));
            if (entityType === "company" && entityId === selectedCompanyRecordId) {
                await loadCompanyTimeline(entityId);
            }
            setMessage("Файл удалён.");
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось удалить файл.");
        }
    };

    useEffect(() => {
        if (selectedCompanyRecordId) {
            loadRecordAttachments("company", selectedCompanyRecordId);
        }
    }, [selectedCompanyRecordId, loadRecordAttachments]);

    useEffect(() => {
        loadCompanyTimeline(selectedCompanyRecordId);
    }, [selectedCompanyRecordId, loadCompanyTimeline]);

    useEffect(() => {
        [
            ["contact", editingContactId],
            ["lead", editingLeadId],
            ["deal", editingDealId],
            ["task", editingTaskId],
        ].forEach(([entityType, entityId]) => {
            if (entityId) {
                loadRecordAttachments(entityType, entityId);
            }
        });
    }, [editingContactId, editingLeadId, editingDealId, editingTaskId, loadRecordAttachments]);

    useEffect(() => {
        const trimmedQuery = searchQuery.trim();
        if (trimmedQuery.length < 2) {
            setSearchResults([]);
            setIsSearching(false);
            return undefined;
        }

        setIsSearching(true);
        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await searchCrm({
                    q: trimmedQuery,
                    include_archived: showArchivedCrm,
                    ...(ownerFilter ? { owner_username: ownerFilter } : {}),
                });
                setSearchResults(response.data);
            } catch (err) {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [searchQuery, showArchivedCrm, ownerFilter]);

    useEffect(() => {
        const trimmedQuery = leadProductQuery.trim();
        if (trimmedQuery.length < 2) {
            setLeadProductResults([]);
            setIsLeadProductSearching(false);
            return undefined;
        }

        setIsLeadProductSearching(true);
        const timeoutId = window.setTimeout(async () => {
            try {
                const response = await searchCatalogProducts({ q: trimmedQuery, limit: 10 });
                setLeadProductResults(response.data || []);
            } catch (err) {
                setLeadProductResults([]);
            } finally {
                setIsLeadProductSearching(false);
            }
        }, 250);

        return () => window.clearTimeout(timeoutId);
    }, [leadProductQuery]);

    const resetCompanyForm = () => {
        setEditingCompanyId(null);
        setCompanyForm(emptyCompany);
    };

    const resetContactForm = () => {
        setEditingContactId(null);
        setContactForm(emptyContact);
    };

    const resetActivityForm = () => {
        setEditingActivityId(null);
        setActivityForm(emptyActivity);
    };

    const resetLeadForm = () => {
        setEditingLeadId(null);
        setLeadForm(emptyLead);
        setLeadConvertForm(emptyLeadConvert);
        setLeadProductQuery("");
        setLeadProductResults([]);
        setIsLeadCreatorOpen(false);
    };

    const openLeadCreator = () => {
        setEditingLeadId(null);
        setLeadForm(emptyLead);
        setLeadConvertForm(emptyLeadConvert);
        setLeadProductQuery("");
        setLeadProductResults([]);
        setIsLeadCreatorOpen(true);
    };

    const resetDealForm = () => {
        setEditingDealId(null);
        setDealForm(emptyDeal);
    };

    const resetTaskForm = () => {
        setEditingTaskId(null);
        setTaskForm(emptyTask);
    };

    const startEditCompany = (company) => {
        setEditingCompanyId(company.id);
        setCompanyForm({
            name: normalizeFormValue(company.name),
            legal_name: normalizeFormValue(company.legal_name),
            status: normalizeFormValue(company.status) || "active",
            phone: normalizeFormValue(company.phone),
            email: normalizeFormValue(company.email),
            website: normalizeFormValue(company.website),
            address: normalizeFormValue(company.address),
        });
    };

    const startEditContact = (contact) => {
        setEditingContactId(contact.id);
        setContactForm({
            company_id: String(contact.company_id || selectedCompany?.id || ""),
            first_name: normalizeFormValue(contact.first_name),
            last_name: normalizeFormValue(contact.last_name),
            position: normalizeFormValue(contact.position),
            phone: normalizeFormValue(contact.phone),
            email: normalizeFormValue(contact.email),
            status: normalizeFormValue(contact.status) || "active",
        });
    };

    const startEditActivity = (activity) => {
        setEditingActivityId(activity.id);
        setActivityForm({
            company_id: activity.company_id ? String(activity.company_id) : "",
            contact_id: activity.contact_id ? String(activity.contact_id) : "",
            type: normalizeFormValue(activity.type) || "note",
            title: normalizeFormValue(activity.title),
            body: normalizeFormValue(activity.body),
        });
    };

    const startEditLead = (lead) => {
        const matchedCompany = companies.find((company) => (
            company.id === lead.converted_company_id || company.name === lead.company_name
        ));
        setEditingLeadId(lead.id);
        setIsLeadCreatorOpen(false);
        setLeadConvertForm({
            company_id: lead.converted_company_id || matchedCompany?.id || "",
            contact_id: lead.converted_contact_id || "",
        });
        setLeadForm({
            title: normalizeFormValue(lead.title),
            company_name: normalizeFormValue(lead.company_name),
            contact_name: normalizeFormValue(lead.contact_name),
            phone: normalizeFormValue(lead.phone),
            email: normalizeFormValue(lead.email),
            source: normalizeFormValue(lead.source) || "manual",
            status: normalizeFormValue(lead.status) || "new",
            score: lead.score ?? 0,
            estimated_amount: lead.estimated_amount ?? "",
            notes: normalizeFormValue(lead.notes),
            items: Array.isArray(lead.items) ? lead.items : [],
        });
        setLeadProductQuery("");
        setLeadProductResults([]);
    };

    const startEditDeal = (deal) => {
        setEditingDealId(deal.id);
        setDealForm({
            title: normalizeFormValue(deal.title),
            company_id: deal.company_id ? String(deal.company_id) : "",
            contact_id: deal.contact_id ? String(deal.contact_id) : "",
            stage: normalizeFormValue(deal.stage) || "new",
            amount: deal.amount ?? "",
            probability: deal.probability ?? 10,
            expected_close_at: deal.expected_close_at ? deal.expected_close_at.slice(0, 10) : "",
            description: normalizeFormValue(deal.description),
        });
    };

    const startEditTask = (task) => {
        setEditingTaskId(task.id);
        setTaskForm({
            title: normalizeFormValue(task.title),
            body: normalizeFormValue(task.body),
            status: normalizeFormValue(task.status) || "open",
            priority: normalizeFormValue(task.priority) || "normal",
            due_at: task.due_at ? task.due_at.slice(0, 16) : "",
            assigned_to_username: normalizeFormValue(task.assigned_to_username),
            company_id: task.company_id ? String(task.company_id) : "",
            contact_id: task.contact_id ? String(task.contact_id) : "",
            deal_id: task.deal_id ? String(task.deal_id) : "",
            lead_id: task.lead_id ? String(task.lead_id) : "",
        });
    };

    const handleSaveCompany = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        try {
            if (editingCompanyId) {
                await editCrmCompany(editingCompanyId, companyForm);
                setMessage("Компания обновлена.");
            } else {
                await createCrmCompany(companyForm);
                setMessage("Компания создана.");
            }
            resetCompanyForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить компанию.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveContact = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = {
            ...contactForm,
            company_id: Number(contactForm.company_id || selectedCompany?.id),
        };
        try {
            if (editingContactId) {
                await editCrmContact(editingContactId, payload);
                setMessage("Контакт обновлён.");
            } else {
                await createCrmContact(payload);
                setMessage("Контакт создан.");
            }
            resetContactForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить контакт.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveActivity = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = {
            ...activityForm,
            company_id: activityForm.company_id ? Number(activityForm.company_id) : selectedCompany?.id || null,
            contact_id: activityForm.contact_id ? Number(activityForm.contact_id) : null,
        };
        try {
            if (editingActivityId) {
                await editCrmActivity(editingActivityId, payload);
                setMessage("Активность обновлена.");
            } else {
                await createCrmActivity(payload);
                setMessage("Активность добавлена.");
            }
            resetActivityForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить активность.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuickNoteSubmit = async (event) => {
        event.preventDefault();
        const body = quickNoteBody.trim();
        if (!selectedCompanyRecordId || !body) {
            return;
        }

        const firstLine = body.split(/\r?\n/).find(Boolean) || "Заметка по клиенту";
        setIsQuickNoteSaving(true);
        try {
            await createCrmActivity({
                company_id: selectedCompanyRecordId,
                contact_id: null,
                type: "note",
                title: firstLine.slice(0, 120),
                body,
            });
            setQuickNoteBody("");
            setMessage("Заметка добавлена в историю клиента.");
            await loadCrm();
            await loadCompanyTimeline(selectedCompanyRecordId);
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось добавить заметку.");
        } finally {
            setIsQuickNoteSaving(false);
        }
    };

    const applyLeadItems = (items) => {
        const calculatedTotal = calculateLeadItemsTotal(items);
        setLeadForm((current) => ({
            ...current,
            items,
            estimated_amount: items.length ? (calculatedTotal || current.estimated_amount) : "",
        }));
    };

    const addLeadCatalogProduct = (product) => {
        const nextItem = productToLeadItem(product);
        const nextIdentity = leadItemIdentity(nextItem);
        const currentItems = Array.isArray(leadForm.items) ? leadForm.items : [];
        const existingIndex = currentItems.findIndex((item) => String(leadItemIdentity(item)) === String(nextIdentity));
        let nextItems;
        if (existingIndex >= 0) {
            nextItems = currentItems.map((item, index) => {
                if (index !== existingIndex) {
                    return item;
                }
                const quantity = Number(normalizeDecimalValue(item.quantity || 1));
                return {
                    ...item,
                    quantity: String((Number.isFinite(quantity) ? quantity : 1) + 1),
                };
            });
        } else {
            nextItems = [...currentItems, nextItem];
        }
        applyLeadItems(nextItems);
        setLeadProductQuery("");
        setLeadProductResults([]);
    };

    const updateLeadItem = (index, updates) => {
        const currentItems = Array.isArray(leadForm.items) ? leadForm.items : [];
        const nextItems = currentItems.map((item, itemIndex) => (
            itemIndex === index ? { ...item, ...updates } : item
        ));
        applyLeadItems(nextItems);
    };

    const removeLeadItem = (index) => {
        const currentItems = Array.isArray(leadForm.items) ? leadForm.items : [];
        applyLeadItems(currentItems.filter((_, itemIndex) => itemIndex !== index));
    };

    const handleSaveLead = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = {
            ...leadForm,
            score: Number(leadForm.score || 0),
            estimated_amount: parseNullableAmount(leadForm.estimated_amount),
            items: Array.isArray(leadForm.items) ? leadForm.items : [],
        };
        try {
            if (editingLeadId) {
                await editCrmLead(editingLeadId, payload);
                setMessage("Лид обновлён.");
            } else {
                await createCrmLead(payload);
                setMessage("Лид создан.");
            }
            resetLeadForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить лид.");
        } finally {
            setIsSaving(false);
        }
    };

    const getAzsStatusDraft = (lead) => (
        azsStatusDrafts[lead.id] || {
            status: lead.azs_status || "in_work",
            customer_comment: "",
        }
    );

    const updateAzsStatusDraft = (leadId, updates) => {
        setAzsStatusDrafts((drafts) => ({
            ...drafts,
            [leadId]: {
                status: drafts[leadId]?.status || "in_work",
                customer_comment: drafts[leadId]?.customer_comment || "",
                ...updates,
            },
        }));
    };

    const handleSendAzsStatus = async (lead) => {
        if (!lead.azs_public_number) {
            return;
        }
        const draft = getAzsStatusDraft(lead);
        setAzsStatusSaving((saving) => ({ ...saving, [lead.id]: true }));
        try {
            const payload = {
                status: draft.status,
                customer_comment: draft.customer_comment.trim() || null,
            };
            const response = await updateAzsRequestStatus(lead.azs_public_number, payload);
            const changedText = response.data?.changed === false ? " Статус уже был применён на сайте." : "";
            setMessage(`Статус заявки ${lead.azs_public_number} отправлен на AZS.${changedText}`);
            setAzsStatusDrafts((drafts) => ({
                ...drafts,
                [lead.id]: { status: draft.status, customer_comment: "" },
            }));
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось отправить статус заявки на AZS.");
        } finally {
            setAzsStatusSaving((saving) => ({ ...saving, [lead.id]: false }));
        }
    };

    const handleConvertLead = async (lead) => {
        if (!window.confirm(`Конвертировать лид "${lead.title}" в компанию, контакт и сделку?`)) {
            return;
        }
        const convertDraft = editingLeadId === lead.id ? leadConvertForm : emptyLeadConvert;
        const activeLeadForm = editingLeadId === lead.id ? leadForm : lead;
        const companyId = convertDraft.company_id ? Number(convertDraft.company_id) : null;
        const contactId = convertDraft.contact_id ? Number(convertDraft.contact_id) : null;
        setIsSaving(true);
        try {
            await convertCrmLead(lead.id, {
                company_id: companyId,
                contact_id: contactId,
                company_name: companyId ? null : activeLeadForm.company_name || lead.company_name || lead.title,
                contact_name: contactId ? null : activeLeadForm.contact_name || lead.contact_name || lead.title,
                deal_title: `Сделка: ${activeLeadForm.title || lead.title}`,
                deal_stage: "new",
                amount: parseNullableAmount(activeLeadForm.estimated_amount ?? lead.estimated_amount),
            });
            setMessage("Лид конвертирован в компанию, контакт и сделку.");
            resetLeadForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось конвертировать лид.");
        } finally {
            setIsSaving(false);
        }
    };

    const getLeadConvertActionLabel = () => {
        if (!leadConvertForm.company_id) {
            return "Создать контрагента и сделку";
        }
        if (!leadConvertForm.contact_id) {
            return "Создать контакт и сделку";
        }
        return "Создать сделку";
    };

    const handleArchiveRecord = async (label, record, archiveFn) => {
        const nextArchived = !record.archived;
        const action = nextArchived ? "архивировать" : "вернуть из архива";
        if (!window.confirm(`Точно ${action} "${label}"?`)) {
            return;
        }
        setIsSaving(true);
        try {
            await archiveFn(record.id, nextArchived);
            setMessage(nextArchived ? "Запись отправлена в архив." : "Запись возвращена из архива.");
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось изменить архивный статус.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteRecord = async (label, deleteFn, warning = "Запись будет удалена без восстановления.") => {
        if (!window.confirm(`${warning}\n\nУдалить "${label}"?`)) {
            return;
        }
        setIsSaving(true);
        try {
            await deleteFn();
            setMessage("Запись удалена.");
            resetCompanyForm();
            resetContactForm();
            resetActivityForm();
            resetLeadForm();
            resetDealForm();
            resetTaskForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось удалить запись.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveDeal = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = {
            ...dealForm,
            company_id: dealForm.company_id ? Number(dealForm.company_id) : selectedCompany?.id || null,
            contact_id: dealForm.contact_id ? Number(dealForm.contact_id) : null,
            amount: dealForm.amount === "" ? null : Number(dealForm.amount),
            probability: Number(dealForm.probability || 0),
            expected_close_at: dealForm.expected_close_at ? new Date(dealForm.expected_close_at).toISOString() : null,
        };
        try {
            if (editingDealId) {
                await editCrmDeal(editingDealId, payload);
                setMessage("Сделка обновлена.");
            } else {
                await createCrmDeal(payload);
                setMessage("Сделка создана.");
            }
            resetDealForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить сделку.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveTask = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        const payload = {
            ...taskForm,
            assigned_to_username: taskForm.assigned_to_username || assignableUsers[0]?.username || "",
            company_id: taskForm.company_id ? Number(taskForm.company_id) : (!editingTaskId ? selectedCompany?.id : null) || null,
            contact_id: taskForm.contact_id ? Number(taskForm.contact_id) : null,
            deal_id: taskForm.deal_id ? Number(taskForm.deal_id) : null,
            lead_id: taskForm.lead_id ? Number(taskForm.lead_id) : null,
            due_at: taskForm.due_at ? new Date(taskForm.due_at).toISOString() : null,
        };
        try {
            if (editingTaskId) {
                await editCrmTask(editingTaskId, payload);
                setMessage("Задача обновлена.");
            } else {
                await createCrmTask(payload);
                setMessage("Задача создана.");
            }
            resetTaskForm();
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось сохранить задачу.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateTaskStatus = async (task, status) => {
        setIsSaving(true);
        try {
            await updateCrmTaskStatus(task.id, status);
            setMessage("Статус задачи обновлён.");
            await loadCrm();
        } catch (err) {
            setMessage(err.response?.data?.detail || "Не удалось обновить статус задачи.");
        } finally {
            setIsSaving(false);
        }
    };

    const focusCompany = (companyId) => {
        if (companyId) {
            setSelectedCompanyId(companyId);
        }
        window.setTimeout(() => {
            document.querySelector(".crm-detail-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const focusForms = () => {
        window.setTimeout(() => {
            document.querySelector(".crm-forms")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const focusLeadEditor = () => {
        window.setTimeout(() => {
            document.querySelector("#crm-lead-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const handleSearchResultClick = (result) => {
        setSearchQuery("");
        setIsSearchOpen(false);
        setSearchResults([]);

        if (result.entity_type === "company") {
            focusCompany(result.company_id || result.id);
            return;
        }

        if (result.company_id) {
            setSelectedCompanyId(result.company_id);
        }

        if (result.entity_type === "contact") {
            const contact = contacts.find((item) => item.id === result.id);
            if (!contact) {
                setMessage("Объект найден, но не загружен в текущем фильтре. Включите архив или сбросьте фильтр.");
                return;
            }
            startEditContact(contact);
            focusForms();
            return;
        }

        if (result.entity_type === "lead") {
            const lead = leads.find((item) => item.id === result.id);
            if (!lead) {
                setMessage("Лид найден, но не загружен в текущем фильтре.");
                return;
            }
            startEditLead(lead);
            focusLeadEditor();
            return;
        }

        if (result.entity_type === "deal") {
            const deal = deals.find((item) => item.id === result.id);
            if (!deal) {
                setMessage("Сделка найдена, но не загружена в текущем фильтре.");
                return;
            }
            startEditDeal(deal);
            focusForms();
            return;
        }

        if (result.entity_type === "task") {
            const task = tasks.find((item) => item.id === result.id);
            if (!task) {
                setMessage("Задача найдена, но не загружена в текущем фильтре.");
                return;
            }
            startEditTask(task);
            focusForms();
        }
    };

    const renderAttachmentPanel = (entityType, entityId, title = "Файлы") => {
        if (!entityType || !entityId) {
            return null;
        }
        const key = attachmentKey(entityType, entityId);
        const attachments = recordAttachments[key] || [];
        const isUploading = Boolean(attachmentUploads[key]);

        return (
            <section className="crm-record-attachments">
                <div className="crm-record-attachments-header">
                    <h3><Paperclip size={16} /> {title}</h3>
                    <button type="button" className="secondary-action compact-action" onClick={() => loadRecordAttachments(entityType, entityId)}>
                        <RefreshCw size={14} /> Обновить
                    </button>
                </div>
                {attachments.length === 0 ? (
                    <p className="empty-copy">Файлов пока нет.</p>
                ) : (
                    <div className="crm-record-attachment-list">
                        {attachments.map((attachment) => (
                            <div className="crm-record-attachment" key={attachment.id}>
                                <div>
                                    <strong>{attachment.original_filename}</strong>
                                    <span>{formatFileSize(attachment.size_bytes)} · {attachment.uploaded_by}</span>
                                </div>
                                <a
                                    href={getCrmRecordAttachmentDownloadUrl(entityType, entityId, attachment.id)}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <Download size={14} /> Скачать
                                </a>
                                <button
                                    type="button"
                                    className="danger-action compact-action"
                                    onClick={() => handleDeleteRecordAttachment(entityType, entityId, attachment)}
                                    title="Удалить файл"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <label className="crm-record-attachment-upload">
                    <Upload size={14} /> {isUploading ? "Загрузка..." : "Загрузить файл"}
                    <input
                        type="file"
                        disabled={isUploading}
                        onChange={(event) => {
                            const [file] = event.target.files || [];
                            handleUploadRecordAttachment(entityType, entityId, file);
                            event.target.value = "";
                        }}
                    />
                </label>
                <span className="task-attachment-hint">До 25 МБ на файл.</span>
            </section>
        );
    };

    const renderTimeline = () => (
        <section className="crm-client-timeline">
            <div className="crm-record-attachments-header">
                <h3><Clock3 size={16} /> История клиента</h3>
                <button
                    type="button"
                    className="secondary-action compact-action"
                    onClick={() => loadCompanyTimeline(selectedCompanyRecordId)}
                    disabled={!selectedCompanyRecordId || isTimelineLoading}
                >
                    <RefreshCw size={14} /> Обновить
                </button>
            </div>
            {isTimelineLoading ? (
                <p className="empty-copy">Загрузка истории...</p>
            ) : timelineItems.length === 0 ? (
                <p className="empty-copy">История пока пустая.</p>
            ) : (
                <div className="crm-timeline-list">
                    {timelineItems.map((item) => (
                        <article className={`crm-timeline-item source-${item.source}`} key={item.id}>
                            <div className="crm-timeline-marker" />
                            <div>
                                <div className="crm-timeline-meta">
                                    <span>{timelineSourceLabels[item.source] || item.source}</span>
                                    <time>{formatDateTime(item.occurred_at)}</time>
                                </div>
                                <strong>{item.title}</strong>
                                <small>
                                    {[item.subtitle, item.status, item.actor].filter(Boolean).join(" · ") || "без деталей"}
                                </small>
                                {item.body && <p>{item.body}</p>}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );

    const renderQuickNote = () => (
        <form className="crm-quick-note" onSubmit={handleQuickNoteSubmit}>
            <div className="crm-record-attachments-header">
                <h3><Pencil size={16} /> Быстрая заметка</h3>
                <span className="task-attachment-hint">Попадёт в историю клиента</span>
            </div>
            <textarea
                value={quickNoteBody}
                onChange={(event) => setQuickNoteBody(event.target.value)}
                placeholder="Например: обсудили поставку, ждём КП до пятницы..."
                rows={3}
            />
            <div className="form-actions">
                <button disabled={!quickNoteBody.trim() || isQuickNoteSaving || !selectedCompanyRecordId}>
                    {isQuickNoteSaving ? "Сохраняем..." : "Добавить заметку"}
                </button>
            </div>
        </form>
    );

    const renderLeadEditor = (lead = null) => (
        <form className="lead-inline-form" onSubmit={handleSaveLead}>
            <div className="lead-inline-form-header">
                <h2><Plus size={16} /> {editingLeadId ? "Редактирование лида" : "Новый лид"}</h2>
                <button type="button" className="icon-action" onClick={resetLeadForm} title="Закрыть редактор">
                    <X size={16} />
                </button>
            </div>
            {lead && (
                <div className="lead-editor-meta">
                    <span>Дата заявки: {formatDateTime(lead.azs_created_at || lead.created_at)}</span>
                    {lead.azs_public_number && <span>{lead.azs_public_number}</span>}
                </div>
            )}
            <div className="lead-form-grid">
                <input value={leadForm.title} onChange={(event) => setLeadForm({ ...leadForm, title: event.target.value })} placeholder="Название лида" required />
                <input value={leadForm.company_name} onChange={(event) => setLeadForm({ ...leadForm, company_name: event.target.value })} placeholder="Компания" />
                <input value={leadForm.contact_name} onChange={(event) => setLeadForm({ ...leadForm, contact_name: event.target.value })} placeholder="Контакт" />
                <input value={leadForm.phone} onChange={(event) => setLeadForm({ ...leadForm, phone: event.target.value })} placeholder="Телефон" />
                <input value={leadForm.email} onChange={(event) => setLeadForm({ ...leadForm, email: event.target.value })} placeholder="Email" />
                <input value={leadForm.source} onChange={(event) => setLeadForm({ ...leadForm, source: event.target.value })} placeholder="Источник" />
                <select value={leadForm.status} onChange={(event) => setLeadForm({ ...leadForm, status: event.target.value })}>
                    <option value="new">Новый</option>
                    <option value="contacted">Связались</option>
                    <option value="qualified">Квалифицирован</option>
                    <option value="lost">Потерян</option>
                </select>
                <input
                    max="100"
                    min="0"
                    type="number"
                    value={leadForm.score}
                    onChange={(event) => setLeadForm({ ...leadForm, score: event.target.value })}
                    placeholder="Оценка, %"
                />
                <input
                    inputMode="decimal"
                    type="text"
                    value={leadForm.estimated_amount}
                    onChange={(event) => setLeadForm({ ...leadForm, estimated_amount: event.target.value })}
                    placeholder="Оценочная сумма"
                />
            </div>
            <textarea value={leadForm.notes} onChange={(event) => setLeadForm({ ...leadForm, notes: event.target.value })} placeholder="Потребность / заметка" />
            <div className="lead-products-box">
                <div className="lead-products-header">
                    <strong>Товары в лиде</strong>
                    <span>{leadForm.items?.length ? `${leadForm.items.length} поз.` : "Можно добавить из каталога"}</span>
                </div>
                <label className="lead-product-search">
                    <Search size={16} />
                    <input
                        value={leadProductQuery}
                        onChange={(event) => setLeadProductQuery(event.target.value)}
                        placeholder="Поиск по каталогу: название, артикул, ссылка"
                    />
                </label>
                {leadProductQuery.trim().length >= 2 && (
                    <div className="lead-product-results">
                        {isLeadProductSearching && <span className="lead-product-search-state">Ищем в каталоге...</span>}
                        {!isLeadProductSearching && leadProductResults.length === 0 && (
                            <span className="lead-product-search-state">Ничего не найдено</span>
                        )}
                        {leadProductResults.map((product) => (
                            <button
                                className="lead-product-result"
                                key={`${product.catalog_product_id || product.external_id || product.url}`}
                                type="button"
                                onClick={() => addLeadCatalogProduct(product)}
                            >
                                {product.primary_picture_url && (
                                    <span className="lead-item-thumb">
                                        <img src={product.primary_picture_url} alt="" loading="lazy" />
                                    </span>
                                )}
                                <span className="lead-product-result-copy">
                                    <strong>{product.name}</strong>
                                    <small>
                                        {[product.external_id && `Арт.: ${product.external_id}`, product.vendor_code, product.price && `${Number(product.price).toLocaleString("ru-RU")} ₽`].filter(Boolean).join(" · ")}
                                    </small>
                                </span>
                                <span className="lead-product-add">Добавить</span>
                            </button>
                        ))}
                    </div>
                )}
                {leadForm.items?.length ? (
                    <div className="lead-products-selected">
                        {leadForm.items.map((item, index) => {
                            const catalogProduct = itemCatalogProduct(item);
                            const itemUrl = itemDisplayUrl(item);
                            const itemThumb = catalogProduct.primary_picture_url;
                            return (
                                <div className="lead-selected-product" key={`${leadItemIdentity(item)}-${index}`}>
                                    {itemThumb && (
                                        <a className="lead-item-thumb" href={itemUrl || itemThumb} target="_blank" rel="noreferrer">
                                            <img src={itemThumb} alt="" loading="lazy" />
                                        </a>
                                    )}
                                    <div className="lead-item-copy">
                                        <strong>{itemDisplayName(item)}</strong>
                                        <small>{[itemDisplayCode(item) && `Арт.: ${itemDisplayCode(item)}`, itemUrl].filter(Boolean).join(" · ")}</small>
                                        {itemUrl && <a href={itemUrl} target="_blank" rel="noreferrer">Открыть товар</a>}
                                    </div>
                                    <div className="lead-item-controls">
                                        <label>
                                            <span>Кол-во</span>
                                            <input
                                                inputMode="decimal"
                                                type="text"
                                                value={item.quantity || ""}
                                                onChange={(event) => updateLeadItem(index, { quantity: event.target.value })}
                                            />
                                        </label>
                                        <label>
                                            <span>Цена</span>
                                            <input
                                                inputMode="decimal"
                                                type="text"
                                                value={item.price || ""}
                                                onChange={(event) => updateLeadItem(index, { price: event.target.value })}
                                            />
                                        </label>
                                        <button className="danger-action compact-action" type="button" onClick={() => removeLeadItem(index)} title="Убрать товар">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="empty-copy">Добавьте товары из каталога, чтобы лид сразу хранил позиции и сумму.</p>
                )}
            </div>
            {editingLeadId && (
                <div className="lead-convert-box">
                    <div>
                        <strong>Контрагент и контакт создаются здесь</strong>
                        <span>Выберите существующие записи или проверьте данные ниже: они будут созданы прямо при переводе лида в сделку.</span>
                    </div>
                    <div className="lead-convert-panel">
                        <select
                            value={leadConvertForm.company_id}
                            onChange={(event) => setLeadConvertForm({ company_id: event.target.value, contact_id: "" })}
                        >
                            <option value="">Создать нового контрагента из лида</option>
                            {companies.map((company) => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                        <select
                            value={leadConvertForm.contact_id}
                            onChange={(event) => setLeadConvertForm({ ...leadConvertForm, contact_id: event.target.value })}
                            disabled={!leadConvertForm.company_id}
                        >
                            <option value="">Создать новый контакт</option>
                            {leadConvertContactOptions.map((contact) => (
                                <option key={contact.id} value={contact.id}>
                                    {[contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.email || contact.phone}
                                </option>
                            ))}
                        </select>
                    </div>
                    {!leadConvertForm.company_id && (
                        <div className="lead-create-inline">
                            <div className="lead-create-inline-title">
                                <strong>Новый контрагент из лида</strong>
                                <span>Эти данные уйдут в новую организацию и первый контакт.</span>
                            </div>
                            <div className="lead-create-inline-grid">
                                <input
                                    value={leadForm.company_name}
                                    onChange={(event) => setLeadForm({ ...leadForm, company_name: event.target.value })}
                                    placeholder="Название контрагента"
                                />
                                <input
                                    value={leadForm.contact_name}
                                    onChange={(event) => setLeadForm({ ...leadForm, contact_name: event.target.value })}
                                    placeholder="Контактное лицо"
                                />
                                <input
                                    value={leadForm.phone}
                                    onChange={(event) => setLeadForm({ ...leadForm, phone: event.target.value })}
                                    placeholder="Телефон"
                                />
                                <input
                                    value={leadForm.email}
                                    onChange={(event) => setLeadForm({ ...leadForm, email: event.target.value })}
                                    placeholder="Email"
                                />
                            </div>
                        </div>
                    )}
                    {leadConvertForm.company_id && !leadConvertForm.contact_id && (
                        <div className="lead-create-inline">
                            <div className="lead-create-inline-title">
                                <strong>Новый контакт в выбранной компании</strong>
                                <span>Если контакт не выбран из списка, CRM создаст его из этих полей.</span>
                            </div>
                            <div className="lead-create-inline-grid contact-only">
                                <input
                                    value={leadForm.contact_name}
                                    onChange={(event) => setLeadForm({ ...leadForm, contact_name: event.target.value })}
                                    placeholder="Контактное лицо"
                                />
                                <input
                                    value={leadForm.phone}
                                    onChange={(event) => setLeadForm({ ...leadForm, phone: event.target.value })}
                                    placeholder="Телефон"
                                />
                                <input
                                    value={leadForm.email}
                                    onChange={(event) => setLeadForm({ ...leadForm, email: event.target.value })}
                                    placeholder="Email"
                                />
                            </div>
                        </div>
                    )}
                    {leadConvertForm.company_id && leadConvertForm.contact_id && (
                        <div className="lead-convert-summary">
                            Сделка будет привязана к выбранному контрагенту и контакту.
                        </div>
                    )}
                </div>
            )}
            <div className="form-actions">
                <button disabled={isSaving}>{editingLeadId ? "Сохранить" : "Создать"}</button>
                {lead && (
                    <button type="button" className="secondary-action" onClick={() => handleConvertLead(lead)} disabled={isSaving || lead.status === "converted"}>
                        <GitBranchPlus size={16} /> {getLeadConvertActionLabel()}
                    </button>
                )}
            </div>
            {editingLeadId && renderAttachmentPanel("lead", editingLeadId, "Файлы лида")}
        </form>
    );

    const metrics = [
        { label: "Компании", value: summary.companies, icon: Building2 },
        { label: "Контакты", value: summary.contacts, icon: Users },
        { label: "Активности", value: summary.activities, icon: CalendarCheck },
        { label: "Открытые лиды", value: summary.open_leads || 0, icon: UserPlus },
        { label: "Сделки", value: summary.deals || 0, icon: BriefcaseBusiness },
        { label: "Активная сумма", value: Number(summary.active_deals_amount || 0).toLocaleString("ru-RU"), icon: CircleDollarSign },
        { label: "Открытые задачи", value: summary.open_tasks || 0, icon: ListChecks },
    ];
    const reportCards = [
        { label: "Просрочено задач", value: reports.overdue_tasks || 0 },
        { label: "На сегодня", value: reports.due_today_tasks || 0 },
        { label: "Срочных открытых", value: reports.urgent_open_tasks || 0 },
        { label: "Активная сумма", value: formatMoney(sumValues(reports.active_deal_amount_by_owner)) },
    ];

    return (
        <main className="crm-shell">
            <section className="crm-top-strip">
                <header className="crm-header">
                    <div className="crm-header-actions">
                    <div
                        className={`crm-search ${(isSearchOpen || searchQuery.trim() || searchResults.length > 0) ? "is-open" : ""}`}
                        onClick={() => setIsSearchOpen(true)}
                    >
                        <Search size={16} />
                        <input
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            onFocus={() => setIsSearchOpen(true)}
                            onBlur={() => {
                                if (!searchQuery.trim() && searchResults.length === 0) {
                                    window.setTimeout(() => setIsSearchOpen(false), 120);
                                }
                            }}
                            placeholder="Поиск по CRM"
                            aria-label="Поиск по CRM"
                        />
                        {(searchQuery.trim().length >= 2 || searchResults.length > 0) && (
                            <div className="crm-search-results">
                                {isSearching ? (
                                    <p>Ищем...</p>
                                ) : searchResults.length === 0 ? (
                                    <p>Ничего не найдено</p>
                                ) : searchResults.map((result) => (
                                    <button key={`${result.entity_type}-${result.id}`} onClick={() => handleSearchResultClick(result)}>
                                        <span>{searchTypeLabels[result.entity_type] || result.entity_type}</span>
                                        <strong>{result.title}</strong>
                                        <small>{result.archived ? "Архив · " : ""}{result.subtitle || "без деталей"}</small>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <label className="crm-owner-filter">
                        <span>Ответственный</span>
                        <select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)}>
                            <option value="">Все</option>
                            {assignableUsers.map((user) => (
                                <option key={user.username} value={user.username}>{user.username}</option>
                            ))}
                        </select>
                    </label>
                    <label className="crm-archive-toggle">
                        <input
                            checked={showArchivedCrm}
                            onChange={(event) => setShowArchivedCrm(event.target.checked)}
                            type="checkbox"
                        />
                        <span>Показать архив</span>
                    </label>
                    <button className="icon-action" onClick={loadCrm} disabled={isLoading} title="Обновить">
                        <RefreshCw size={16} />
                    </button>
                    </div>
                </header>

                <section className="crm-metrics">
                    {metrics.map(({ label, value, icon: Icon }) => (
                        <article className="crm-metric" key={label}>
                            <span className="crm-metric-icon"><Icon size={15} /></span>
                            <strong>{value}</strong>
                            <span>{label}</span>
                        </article>
                    ))}
                </section>
            </section>
            {message && <p className="call-notice">{message}</p>}

            <section className="crm-leads-priority">
                <section className="crm-lead-board">
                <div className="crm-section-title">
                    <h2><UserPlus size={18} /> Лиды</h2>
                    <div className="crm-section-title-actions">
                        <span>{visibleLeads.length}</span>
                        <button className="secondary-action compact-action" type="button" onClick={openLeadCreator}>
                            <Plus size={14} /> Новый лид
                        </button>
                    </div>
                </div>
                {isLeadCreatorOpen && (
                    <div className="lead-create-editor" id="crm-lead-editor">
                        {renderLeadEditor()}
                    </div>
                )}
                {visibleLeads.length === 0 ? (
                    <p className="empty-copy">Пока нет лидов. Добавьте первый входящий интерес.</p>
                ) : (
                    <div className="lead-grid">
                        {visibleLeads.map((lead) => (
                            <article className={`lead-card lead-status-${lead.status} ${lead.archived ? "is-archived" : ""}`} key={lead.id}>
                                <div className="lead-main-copy">
                                    <span>{lead.archived ? "Архив · " : ""}{leadStatusLabels[lead.status] || lead.status}</span>
                                    <strong>{lead.title}</strong>
                                    <small>Контрагент: {lead.company_name || "создать нового"}</small>
                                    <small>Контакт: {lead.contact_name || lead.azs_customer_name || lead.azs_customer_contact || "уточнить"}</small>
                                    {lead.azs_created_at && (
                                        <small>Дата заявки: {formatDateTime(lead.azs_created_at)}</small>
                                    )}
                                    {(lead.phone || lead.email || lead.azs_customer_contact) && (
                                        <small>{[lead.phone, lead.email, lead.azs_customer_contact].filter(Boolean).join(" · ")}</small>
                                    )}
                                    {leadRequestTextLines(lead).length > 0 && (
                                        <div className="lead-details-preview">
                                            <strong>Текст заявки</strong>
                                            {leadRequestTextLines(lead).map((line, index) => (
                                                <span key={`${lead.id}-detail-${index}`}>{line}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="lead-items-preview">
                                    <div className="lead-meta">
                                        <span>{lead.source}</span>
                                        <span>{formatDateTime(lead.azs_created_at || lead.created_at)}</span>
                                        <span>{lead.score}%</span>
                                        <span>{lead.estimated_amount ? `${Number(lead.estimated_amount).toLocaleString("ru-RU")} ₽` : "без суммы"}</span>
                                    </div>
                                    <strong>Товары / позиции</strong>
                                    {lead.azs_items?.length ? (
                                        lead.azs_items.slice(0, 3).map((item, index) => {
                                            const catalogProduct = itemCatalogProduct(item);
                                            const itemUrl = itemDisplayUrl(item);
                                            const params = itemDisplayParams(item);
                                            return (
                                                <div className="lead-item-row" key={`${lead.id}-item-${index}`}>
                                                    {catalogProduct.primary_picture_url && (
                                                        <a className="lead-item-thumb" href={itemUrl || catalogProduct.primary_picture_url} target="_blank" rel="noreferrer">
                                                            <img src={catalogProduct.primary_picture_url} alt="" loading="lazy" />
                                                        </a>
                                                    )}
                                                    <div className="lead-item-copy">
                                                        <span>{itemDisplayName(item)}</span>
                                                        <small>{[itemDisplayCode(item) && `Арт.: ${itemDisplayCode(item)}`, itemDisplayAmount(item)].filter(Boolean).join(" · ")}</small>
                                                        {params && <small>Комплектация: {params}</small>}
                                                        {itemUrl && <a href={itemUrl} target="_blank" rel="noreferrer">Открыть товар</a>}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        leadDetailsLines(lead).filter((line) => /^\d+\./.test(line)).slice(0, 3).map((line, index) => (
                                            <div className="lead-item-row" key={`${lead.id}-note-item-${index}`}>
                                                <span>{line}</span>
                                            </div>
                                        ))
                                    )}
                                    {lead.azs_items?.length > 3 && <small>Ещё позиций: {lead.azs_items.length - 3}</small>}
                                    {lead.azs_page_url && (
                                        <a href={lead.azs_page_url} target="_blank" rel="noreferrer">Страница заявки</a>
                                    )}
                                </div>
                                {lead.azs_public_number && (
                                    <div className="azs-request-panel">
                                        <div className="azs-request-meta">
                                            <span>{lead.azs_public_number}</span>
                                            <span>{azsStatusLabels[lead.azs_status] || lead.azs_status || "Новая"}</span>
                                            <span>{azsPublicStatusLabels[lead.azs_public_status] || lead.azs_public_status || "Получена"}</span>
                                            {lead.azs_created_at && <span>{formatDateTime(lead.azs_created_at)}</span>}
                                        </div>
                                        <div className="azs-status-form">
                                            <select
                                                value={getAzsStatusDraft(lead).status}
                                                onChange={(event) => updateAzsStatusDraft(lead.id, { status: event.target.value })}
                                            >
                                                {azsStatusOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                            <textarea
                                                value={getAzsStatusDraft(lead).customer_comment}
                                                onChange={(event) => updateAzsStatusDraft(lead.id, { customer_comment: event.target.value })}
                                                placeholder="Комментарий клиенту на сайте"
                                            />
                                            <button
                                                type="button"
                                                className="secondary-action compact-action"
                                                onClick={() => handleSendAzsStatus(lead)}
                                                disabled={Boolean(azsStatusSaving[lead.id])}
                                            >
                                                {azsStatusSaving[lead.id] ? <RefreshCw size={14} /> : <MessageCircle size={14} />}
                                                Отправить
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div className="lead-actions">
                                    <button className="secondary-action compact-action" onClick={() => { startEditLead(lead); focusLeadEditor(); }} disabled={lead.status === "converted"}>
                                        <Pencil size={14} /> Править
                                    </button>
                                    <button
                                        className="secondary-action compact-action"
                                        onClick={() => handleArchiveRecord(lead.title, lead, archiveCrmLead)}
                                        disabled={isSaving}
                                    >
                                        {lead.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                                        {lead.archived ? "Вернуть" : "Архив"}
                                    </button>
                                    <button className="compact-action" onClick={() => { startEditLead(lead); focusLeadEditor(); }} disabled={isSaving || lead.status === "converted"}>
                                        <GitBranchPlus size={14} /> Привязать
                                    </button>
                                    <button
                                        className="danger-action compact-action"
                                        onClick={() => handleDeleteRecord(lead.title, () => deleteCrmLead(lead.id))}
                                        disabled={isSaving}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                {editingLeadId === lead.id && (
                                    <div className="lead-inline-editor" id="crm-lead-editor">
                                        {renderLeadEditor(lead)}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </section>
            </section>


            <section className="crm-reports">
                <div className="crm-section-title">
                    <h2><ListChecks size={18} /> Контроль работы</h2>
                    <span>{ownerFilter || "вся команда"}</span>
                </div>
                <div className="crm-report-grid">
                    {reportCards.map((card) => (
                        <article className="crm-report-card" key={card.label}>
                            <strong>{card.value}</strong>
                            <span>{card.label}</span>
                        </article>
                    ))}
                </div>
                <div className="crm-report-buckets">
                    <article>
                        <h3>Сделки по стадиям</h3>
                        {dealStages.map((stage) => (
                            <p key={stage.value}>
                                <span>{stage.label}</span>
                                <strong>{reports.deals_by_stage?.[stage.value] || 0}</strong>
                            </p>
                        ))}
                    </article>
                    <article>
                        <h3>Задачи по статусам</h3>
                        {Object.entries(taskStatusLabels).map(([value, label]) => (
                            <p key={value}>
                                <span>{label}</span>
                                <strong>{reports.tasks_by_status?.[value] || 0}</strong>
                            </p>
                        ))}
                    </article>
                    <article>
                        <h3>Нагрузка</h3>
                        {assignableUsers.length === 0 ? (
                            <p><span>Нет сотрудников</span><strong>0</strong></p>
                        ) : assignableUsers.map((user) => (
                            <p key={user.username}>
                                <span>{user.username}</span>
                                <strong>{reports.tasks_by_owner?.[user.username] || 0}</strong>
                            </p>
                        ))}
                    </article>
                </div>
            </section>

            <section className="crm-workspace">
                <aside className="crm-list-panel">
                    <div className="crm-section-title">
                        <h2>Компании</h2>
                        <span>{companies.length}</span>
                    </div>
                    {isLoading ? (
                        <p>Загрузка...</p>
                    ) : companies.length === 0 ? (
                        <p className="empty-copy">Пока нет компаний.</p>
                    ) : companies.map((company) => (
                        <button
                            className={`crm-company-row ${selectedCompany?.id === company.id ? "is-selected" : ""} ${company.archived ? "is-archived" : ""}`}
                            key={company.id}
                            onClick={() => setSelectedCompanyId(company.id)}
                        >
                            <strong>{company.name}</strong>
                            <span>{company.archived ? "Архив · " : ""}{company.contacts_count} контактов · {company.activities_count} активностей</span>
                        </button>
                    ))}
                </aside>

                <section className="crm-detail-panel">
                    <div className="crm-section-title">
                        <h2>{selectedCompany?.name || "Карточка компании"}</h2>
                        {selectedCompany && (
                            <div className="record-actions">
                                <button className="secondary-action compact-action" onClick={() => startEditCompany(selectedCompany)}>
                                    <Pencil size={15} /> Редактировать
                                </button>
                                <button
                                    className="secondary-action compact-action"
                                    onClick={() => handleArchiveRecord(selectedCompany.name, selectedCompany, archiveCrmCompany)}
                                    disabled={isSaving}
                                >
                                    {selectedCompany.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                                    {selectedCompany.archived ? "Вернуть" : "В архив"}
                                </button>
                                <button
                                    className="danger-action compact-action"
                                    onClick={() => handleDeleteRecord(
                                        selectedCompany.name,
                                        () => deleteCrmCompany(selectedCompany.id),
                                        "Компания будет удалена вместе со связанными контактами, активностями и сделками."
                                    )}
                                    disabled={isSaving}
                                >
                                    <Trash2 size={14} /> Удалить
                                </button>
                            </div>
                        )}
                    </div>
                    {selectedCompany ? (
                        <>
                            <div className="crm-detail-grid">
                                <p><strong>Статус</strong><span>{selectedCompany.archived ? "Архив" : statusLabels[selectedCompany.status] || selectedCompany.status}</span></p>
                                <p><strong>Юр. название</strong><span>{selectedCompany.legal_name || "не указано"}</span></p>
                                <p><strong>Телефон</strong><span>{selectedCompany.phone || "не указан"}</span></p>
                                <p><strong>Email</strong><span>{selectedCompany.email || "не указан"}</span></p>
                                <p><strong>Сайт</strong><span>{selectedCompany.website || "не указан"}</span></p>
                                <p><strong>Адрес</strong><span>{selectedCompany.address || "не указан"}</span></p>
                            </div>

                            <div className="client-card-summary">
                                <article>
                                    <strong>{selectedCompanyContacts.length}</strong>
                                    <span>контактов</span>
                                </article>
                                <article>
                                    <strong>{visibleSelectedCompanyDeals.length}</strong>
                                    <span>сделок</span>
                                </article>
                                <article>
                                    <strong>{selectedCompanyLeads.length}</strong>
                                    <span>лидов</span>
                                </article>
                                <article>
                                    <strong>{visibleSelectedCompanyOpenTasks.length}</strong>
                                    <span>открытых задач</span>
                                </article>
                                <article>
                                    <strong>{formatMoney(selectedCompanyActiveDealAmount)}</strong>
                                    <span>активная сумма</span>
                                </article>
                            </div>

                            <div className="client-card-sections">
                                <section>
                                    <div className="client-card-heading">
                                        <h3>Сделки клиента</h3>
                                        <span>{visibleSelectedCompanyDeals.length}</span>
                                    </div>
                                    {visibleSelectedCompanyDeals.length === 0 ? (
                                        <p className="empty-copy">Сделок пока нет.</p>
                                    ) : visibleSelectedCompanyDeals.slice(0, 4).map((deal) => (
                                        <button className="client-card-row" key={deal.id} onClick={() => startEditDeal(deal)}>
                                            <strong>{deal.title}</strong>
                                            <span>{dealStages.find((stage) => stage.value === deal.stage)?.label || deal.stage} · {formatMoney(deal.amount)}</span>
                                        </button>
                                    ))}
                                </section>

                                <section>
                                    <div className="client-card-heading">
                                        <h3>Лиды</h3>
                                        <span>{selectedCompanyLeads.length}</span>
                                    </div>
                                    {selectedCompanyLeads.length === 0 ? (
                                        <p className="empty-copy">Лидов по клиенту нет.</p>
                                    ) : selectedCompanyLeads.slice(0, 4).map((lead) => (
                                        <button className="client-card-row" key={lead.id} onClick={() => { startEditLead(lead); focusLeadEditor(); }} disabled={lead.status === "converted"}>
                                            <strong>{lead.title}</strong>
                                            <span>{leadStatusLabels[lead.status] || lead.status} · {lead.source}</span>
                                        </button>
                                    ))}
                                </section>

                                <section>
                                    <div className="client-card-heading">
                                        <h3>Последние активности</h3>
                                        <span>{selectedCompanyActivities.length}</span>
                                    </div>
                                    {selectedCompanyActivities.length === 0 ? (
                                        <p className="empty-copy">Активностей пока нет.</p>
                                    ) : selectedCompanyActivities.slice(0, 4).map((activity) => (
                                        <button className="client-card-row" key={activity.id} onClick={() => startEditActivity(activity)}>
                                            <strong>{activity.title}</strong>
                                            <span>{activityTypeLabels[activity.type] || activity.type}</span>
                                        </button>
                                    ))}
                                </section>

                                <section>
                                    <div className="client-card-heading">
                                        <h3>Задачи</h3>
                                        <span>{visibleSelectedCompanyOpenTasks.length}</span>
                                    </div>
                                    {visibleSelectedCompanyTasks.length === 0 ? (
                                        <p className="empty-copy">Задач по клиенту пока нет.</p>
                                    ) : visibleSelectedCompanyTasks.slice(0, 5).map((task) => (
                                        <button className="client-card-row" key={task.id} onClick={() => startEditTask(task)}>
                                            <strong>{task.title}</strong>
                                            <span>{taskStatusLabels[task.status] || task.status} · {task.assigned_to_username}</span>
                                        </button>
                                    ))}
                                </section>
                            </div>
                            {renderQuickNote()}
                            {renderTimeline()}
                            {renderAttachmentPanel("company", selectedCompany.id, "Файлы компании")}
                        </>
                    ) : (
                        <p className="empty-copy">Выберите или создайте компанию.</p>
                    )}

                    <div className="crm-section-title">
                        <h2>Контакты</h2>
                        <span>{selectedCompanyContacts.length}</span>
                    </div>
                    <div className="crm-contact-list">
                        {selectedCompanyContacts.map((contact) => (
                            <article className={`crm-contact-row ${contact.archived ? "is-archived" : ""}`} key={contact.id}>
                                <div>
                                    <strong>{contact.first_name} {contact.last_name}</strong>
                                    <span>{contact.position || contact.company_name}</span>
                                    <small>{contact.archived ? "Архив · " : ""}{contact.phone || contact.email || "нет связи"}</small>
                                </div>
                                <div className="record-actions">
                                    <button className="secondary-action compact-action" onClick={() => startEditContact(contact)}>
                                        <Pencil size={14} /> Править
                                    </button>
                                    <button
                                        className="secondary-action compact-action"
                                        onClick={() => handleArchiveRecord(`${contact.first_name} ${contact.last_name || ""}`.trim(), contact, archiveCrmContact)}
                                        disabled={isSaving}
                                    >
                                        {contact.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                                        {contact.archived ? "Вернуть" : "Архив"}
                                    </button>
                                    <button
                                        className="danger-action compact-action"
                                        onClick={() => handleDeleteRecord(
                                            `${contact.first_name} ${contact.last_name || ""}`.trim(),
                                            () => deleteCrmContact(contact.id),
                                            "Контакт будет удалён; связанные сделки останутся без контактного лица."
                                        )}
                                        disabled={isSaving}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                <aside className="crm-list-panel">
                    <div className="crm-section-title">
                        <h2>Активности</h2>
                        <span>{selectedCompanyActivities.length}</span>
                    </div>
                    <div className="crm-activity-list">
                        {selectedCompanyActivities.length === 0 ? (
                            <p className="empty-copy">По выбранной компании активностей пока нет.</p>
                        ) : selectedCompanyActivities.slice(0, 12).map((activity) => (
                            <article className={`crm-activity-row ${activity.archived ? "is-archived" : ""}`} key={activity.id}>
                                <div>
                                    <span>{activityTypeLabels[activity.type] || activity.type}</span>
                                    <strong>{activity.title}</strong>
                                    <small>{activity.archived ? "Архив · " : ""}{activity.company_name || activity.contact_name || "без связи"}</small>
                                </div>
                                <div className="record-actions">
                                    <button className="secondary-action compact-action" onClick={() => startEditActivity(activity)}>
                                        <Pencil size={14} /> Править
                                    </button>
                                    <button
                                        className="secondary-action compact-action"
                                        onClick={() => handleArchiveRecord(activity.title, activity, archiveCrmActivity)}
                                        disabled={isSaving}
                                    >
                                        {activity.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                                        {activity.archived ? "Вернуть" : "Архив"}
                                    </button>
                                    <button
                                        className="danger-action compact-action"
                                        onClick={() => handleDeleteRecord(activity.title, () => deleteCrmActivity(activity.id))}
                                        disabled={isSaving}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </aside>
            </section>

            <section className="crm-deal-board">
                <div className="crm-section-title">
                    <h2><BriefcaseBusiness size={18} /> Сделки</h2>
                    <span>{visibleSelectedCompanyDeals.length}</span>
                </div>
                <div className="deal-stage-grid">
                    {dealStages.map((stage) => {
                        const stageDeals = visibleSelectedCompanyDeals.filter((deal) => deal.stage === stage.value);
                        return (
                            <article className="deal-stage-column" key={stage.value}>
                                <div className="deal-stage-header">
                                    <strong>{stage.label}</strong>
                                    <span>{stageDeals.length}</span>
                                </div>
                                {stageDeals.length === 0 ? (
                                    <p className="empty-copy">Пока пусто</p>
                                ) : stageDeals.map((deal) => (
                                    <button className={`deal-card ${deal.archived ? "is-archived" : ""}`} key={deal.id} onClick={() => startEditDeal(deal)}>
                                        <strong>{deal.title}</strong>
                                        <span>{deal.archived ? "Архив · " : ""}{deal.company_name || "без компании"}</span>
                                        <small>
                                            {deal.amount ? `${Number(deal.amount).toLocaleString("ru-RU")} ₽` : "сумма не указана"}
                                            {" · "}
                                            {deal.probability}%
                                        </small>
                                    </button>
                                ))}
                            </article>
                        );
                    })}
                </div>
            </section>

            <section className="crm-task-board">
                <div className="crm-section-title">
                    <h2><ListChecks size={18} /> CRM-задачи</h2>
                    <span>{visibleSelectedCompanyTasks.length}</span>
                </div>
                {visibleSelectedCompanyTasks.length === 0 ? (
                    <p className="empty-copy">По выбранному клиенту задач пока нет. Создайте задачу в форме ниже и привяжите её к компании, сделке, лиду или контакту.</p>
                ) : (
                    <div className="crm-task-list">
                        {visibleSelectedCompanyTasks.map((task) => (
                            <article className={`crm-task-row task-priority-${task.priority} ${task.archived ? "is-archived" : ""}`} key={task.id}>
                                <div>
                                    <span>{taskPriorityLabels[task.priority] || task.priority} · {task.assigned_to_username}</span>
                                    <strong>{task.title}</strong>
                                    <small>
                                        {task.due_at ? `Срок: ${new Date(task.due_at).toLocaleString("ru-RU")}` : "Без срока"}
                                        {task.deal_title ? ` · Сделка: ${task.deal_title}` : ""}
                                        {task.lead_title ? ` · Лид: ${task.lead_title}` : ""}
                                    </small>
                                </div>
                                <div className="record-actions">
                                    <select
                                        value={task.status}
                                        onChange={(event) => handleUpdateTaskStatus(task, event.target.value)}
                                        disabled={isSaving || task.archived}
                                        title="Статус задачи"
                                    >
                                        {Object.entries(taskStatusLabels).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                    <button className="secondary-action compact-action" onClick={() => startEditTask(task)}>
                                        <Pencil size={14} /> Править
                                    </button>
                                    <button
                                        className="secondary-action compact-action"
                                        onClick={() => handleArchiveRecord(task.title, task, archiveCrmTask)}
                                        disabled={isSaving}
                                    >
                                        {task.archived ? <RotateCcw size={14} /> : <Archive size={14} />}
                                        {task.archived ? "Вернуть" : "Архив"}
                                    </button>
                                    <button
                                        className="danger-action compact-action"
                                        onClick={() => handleDeleteRecord(task.title, () => deleteCrmTask(task.id))}
                                        disabled={isSaving}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <section className="crm-forms">
                <form onSubmit={handleSaveCompany}>
                    <h2><Plus size={16} /> {editingCompanyId ? "Редактирование компании" : "Компания"}</h2>
                    <input value={companyForm.name} onChange={(event) => setCompanyForm({ ...companyForm, name: event.target.value })} placeholder="Название" required />
                    <input value={companyForm.legal_name} onChange={(event) => setCompanyForm({ ...companyForm, legal_name: event.target.value })} placeholder="Юридическое название" />
                    <select value={companyForm.status} onChange={(event) => setCompanyForm({ ...companyForm, status: event.target.value })}>
                        <option value="active">Активна</option>
                        <option value="paused">На паузе</option>
                        <option value="archived">Архив</option>
                    </select>
                    <input value={companyForm.phone} onChange={(event) => setCompanyForm({ ...companyForm, phone: event.target.value })} placeholder="Телефон" />
                    <input value={companyForm.email} onChange={(event) => setCompanyForm({ ...companyForm, email: event.target.value })} placeholder="Email" />
                    <input value={companyForm.website} onChange={(event) => setCompanyForm({ ...companyForm, website: event.target.value })} placeholder="Сайт" />
                    <input value={companyForm.address} onChange={(event) => setCompanyForm({ ...companyForm, address: event.target.value })} placeholder="Адрес" />
                    <div className="form-actions">
                        <button disabled={isSaving}>{editingCompanyId ? "Сохранить" : "Создать"}</button>
                        {editingCompanyId && (
                            <button type="button" className="secondary-action" onClick={resetCompanyForm}><X size={16} /> Отмена</button>
                        )}
                    </div>
                </form>

                <form onSubmit={handleSaveContact}>
                    <h2><Plus size={16} /> {editingContactId ? "Редактирование контакта" : "Контакт"}</h2>
                    <select value={contactForm.company_id || selectedCompany?.id || ""} onChange={(event) => setContactForm({ ...contactForm, company_id: event.target.value })} required>
                        <option value="">Компания</option>
                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                    </select>
                    <input value={contactForm.first_name} onChange={(event) => setContactForm({ ...contactForm, first_name: event.target.value })} placeholder="Имя" required />
                    <input value={contactForm.last_name} onChange={(event) => setContactForm({ ...contactForm, last_name: event.target.value })} placeholder="Фамилия" />
                    <input value={contactForm.position} onChange={(event) => setContactForm({ ...contactForm, position: event.target.value })} placeholder="Должность" />
                    <input value={contactForm.phone} onChange={(event) => setContactForm({ ...contactForm, phone: event.target.value })} placeholder="Телефон" />
                    <input value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} placeholder="Email" />
                    <select value={contactForm.status} onChange={(event) => setContactForm({ ...contactForm, status: event.target.value })}>
                        <option value="active">Активен</option>
                        <option value="paused">На паузе</option>
                        <option value="archived">Архив</option>
                    </select>
                    <div className="form-actions">
                        <button disabled={isSaving || companies.length === 0}>{editingContactId ? "Сохранить" : "Создать"}</button>
                        {editingContactId && (
                            <button type="button" className="secondary-action" onClick={resetContactForm}><X size={16} /> Отмена</button>
                        )}
                    </div>
                    {editingContactId && renderAttachmentPanel("contact", editingContactId, "Файлы контакта")}
                </form>

                <form onSubmit={handleSaveActivity}>
                    <h2><Plus size={16} /> {editingActivityId ? "Редактирование активности" : "Активность"}</h2>
                    <select value={activityForm.company_id || selectedCompany?.id || ""} onChange={(event) => setActivityForm({ ...activityForm, company_id: event.target.value })}>
                        <option value="">Компания</option>
                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                    </select>
                    <select value={activityForm.contact_id} onChange={(event) => setActivityForm({ ...activityForm, contact_id: event.target.value })}>
                        <option value="">Контакт</option>
                        {contacts.map((contact) => (
                            <option key={contact.id} value={contact.id}>
                                {contact.first_name} {contact.last_name}
                            </option>
                        ))}
                    </select>
                    <select value={activityForm.type} onChange={(event) => setActivityForm({ ...activityForm, type: event.target.value })}>
                        <option value="note">Заметка</option>
                        <option value="call">Звонок</option>
                        <option value="meeting">Встреча</option>
                        <option value="task">Задача</option>
                    </select>
                    <input value={activityForm.title} onChange={(event) => setActivityForm({ ...activityForm, title: event.target.value })} placeholder="Тема" required />
                    <textarea value={activityForm.body} onChange={(event) => setActivityForm({ ...activityForm, body: event.target.value })} placeholder="Описание" />
                    <div className="form-actions">
                        <button disabled={isSaving}>{editingActivityId ? "Сохранить" : "Добавить"}</button>
                        {editingActivityId && (
                            <button type="button" className="secondary-action" onClick={resetActivityForm}><X size={16} /> Отмена</button>
                        )}
                    </div>
                </form>

                <form onSubmit={handleSaveDeal}>
                    <h2><Plus size={16} /> {editingDealId ? "Редактирование сделки" : "Сделка"}</h2>
                    <input value={dealForm.title} onChange={(event) => setDealForm({ ...dealForm, title: event.target.value })} placeholder="Название сделки" required />
                    <select
                        value={dealCompanyId}
                        onChange={(event) => setDealForm({ ...dealForm, company_id: event.target.value, contact_id: "" })}
                    >
                        <option value="">Компания</option>
                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                    </select>
                    <select value={dealForm.contact_id} onChange={(event) => setDealForm({ ...dealForm, contact_id: event.target.value })}>
                        <option value="">Контакт</option>
                        {dealContactOptions.map((contact) => (
                            <option key={contact.id} value={contact.id}>
                                {contact.first_name} {contact.last_name}
                            </option>
                        ))}
                    </select>
                    <select value={dealForm.stage} onChange={(event) => setDealForm({ ...dealForm, stage: event.target.value })}>
                        {dealStages.map((stage) => (
                            <option key={stage.value} value={stage.value}>{stage.label}</option>
                        ))}
                    </select>
                    <input
                        min="0"
                        step="0.01"
                        type="number"
                        value={dealForm.amount}
                        onChange={(event) => setDealForm({ ...dealForm, amount: event.target.value })}
                        placeholder="Сумма"
                    />
                    <input
                        max="100"
                        min="0"
                        type="number"
                        value={dealForm.probability}
                        onChange={(event) => setDealForm({ ...dealForm, probability: event.target.value })}
                        placeholder="Вероятность, %"
                    />
                    <input
                        type="date"
                        value={dealForm.expected_close_at}
                        onChange={(event) => setDealForm({ ...dealForm, expected_close_at: event.target.value })}
                    />
                    <textarea value={dealForm.description} onChange={(event) => setDealForm({ ...dealForm, description: event.target.value })} placeholder="Описание сделки" />
                    <div className="form-actions">
                        <button disabled={isSaving}>{editingDealId ? "Сохранить" : "Создать"}</button>
                        {editingDealId && (
                            <button type="button" className="secondary-action" onClick={resetDealForm}><X size={16} /> Отмена</button>
                        )}
                        {editingDeal && (
                            <button
                                type="button"
                                className="secondary-action"
                                onClick={() => handleArchiveRecord(editingDeal.title, editingDeal, archiveCrmDeal)}
                                disabled={isSaving}
                            >
                                {editingDeal.archived ? <RotateCcw size={16} /> : <Archive size={16} />}
                                {editingDeal.archived ? "Вернуть" : "В архив"}
                            </button>
                        )}
                        {editingDeal && (
                            <button
                                type="button"
                                className="danger-action"
                                onClick={() => handleDeleteRecord(editingDeal.title, () => deleteCrmDeal(editingDeal.id))}
                                disabled={isSaving}
                            >
                                <Trash2 size={16} /> Удалить
                            </button>
                        )}
                    </div>
                    {editingDealId && renderAttachmentPanel("deal", editingDealId, "Файлы сделки")}
                </form>

                <form onSubmit={handleSaveTask}>
                    <h2><Plus size={16} /> {editingTaskId ? "Редактирование задачи" : "CRM-задача"}</h2>
                    <input value={taskForm.title} onChange={(event) => setTaskForm({ ...taskForm, title: event.target.value })} placeholder="Что нужно сделать" required />
                    <select value={taskForm.assigned_to_username} onChange={(event) => setTaskForm({ ...taskForm, assigned_to_username: event.target.value })} required>
                        <option value="">Исполнитель</option>
                        {assignableUsers.map((user) => (
                            <option key={user.username} value={user.username}>{user.username}</option>
                        ))}
                    </select>
                    <select value={taskForm.status} onChange={(event) => setTaskForm({ ...taskForm, status: event.target.value })}>
                        {Object.entries(taskStatusLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <select value={taskForm.priority} onChange={(event) => setTaskForm({ ...taskForm, priority: event.target.value })}>
                        {Object.entries(taskPriorityLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <input
                        type="datetime-local"
                        value={taskForm.due_at}
                        onChange={(event) => setTaskForm({ ...taskForm, due_at: event.target.value })}
                    />
                    <select
                        value={taskCompanyId}
                        onChange={(event) => setTaskForm({ ...taskForm, company_id: event.target.value, contact_id: "", deal_id: "" })}
                    >
                        <option value="">Компания</option>
                        {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                    </select>
                    <select value={taskForm.contact_id} onChange={(event) => setTaskForm({ ...taskForm, contact_id: event.target.value })}>
                        <option value="">Контакт</option>
                        {taskContactOptions.map((contact) => (
                            <option key={contact.id} value={contact.id}>
                                {contact.first_name} {contact.last_name}
                            </option>
                        ))}
                    </select>
                    <select value={taskForm.deal_id} onChange={(event) => setTaskForm({ ...taskForm, deal_id: event.target.value })}>
                        <option value="">Сделка</option>
                        {taskDealOptions.map((deal) => <option key={deal.id} value={deal.id}>{deal.title}</option>)}
                    </select>
                    <select value={taskForm.lead_id} onChange={(event) => setTaskForm({ ...taskForm, lead_id: event.target.value })}>
                        <option value="">Лид</option>
                        {leads.map((lead) => <option key={lead.id} value={lead.id}>{lead.title}</option>)}
                    </select>
                    <textarea value={taskForm.body} onChange={(event) => setTaskForm({ ...taskForm, body: event.target.value })} placeholder="Описание / чеклист" />
                    <div className="form-actions">
                        <button disabled={isSaving || assignableUsers.length === 0}>{editingTaskId ? "Сохранить" : "Создать"}</button>
                        {editingTaskId && (
                            <button type="button" className="secondary-action" onClick={resetTaskForm}><X size={16} /> Отмена</button>
                        )}
                        {editingTask && (
                            <button
                                type="button"
                                className="secondary-action"
                                onClick={() => handleArchiveRecord(editingTask.title, editingTask, archiveCrmTask)}
                                disabled={isSaving}
                            >
                                {editingTask.archived ? <RotateCcw size={16} /> : <Archive size={16} />}
                                {editingTask.archived ? "Вернуть" : "В архив"}
                            </button>
                        )}
                        {editingTask && (
                            <button
                                type="button"
                                className="danger-action"
                                onClick={() => handleDeleteRecord(editingTask.title, () => deleteCrmTask(editingTask.id))}
                                disabled={isSaving}
                            >
                                <Trash2 size={16} /> Удалить
                            </button>
                        )}
                    </div>
                    {editingTaskId && renderAttachmentPanel("task", editingTaskId, "Файлы CRM-задачи")}
                </form>
            </section>

            <section className="crm-roadmap">
                <h2><CircleDollarSign size={18} /> Следующие модули</h2>
                <p>Следом усилим лиды, карточку клиента, задачи по сделкам и отчёты по воронке: источник, ответственный, сроки, конверсия и активность команды.</p>
            </section>
        </main>
    );
};

export default CrmHome;
